/**
 * Agent installation content shared by the Add agent flow: copyable code blocks, the Windows (Hyper-V)
 * and Docker Engine steps, and the build/compose/start commands. Every command line stays short because
 * long pasted lines were split by the terminal during the 2026-10-29 WSL test.
 */

import { useState } from 'react';
import { Copy } from 'lucide-react';
import { coreApiMode } from '@/config/deployment';
import { downloadAgentPackage } from '@/api/agents';
import { apiError } from '@/utils/apiError';

export function CodeBlock({ children }: { children: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  return (
    <div className="relative group mt-1.5">
      <pre className="rounded bg-gray-900 text-green-400 text-xs p-3 overflow-x-auto font-mono whitespace-pre">
        {children}
      </pre>
      <button
        onClick={copy}
        className="absolute top-2 right-2 rounded p-1 text-gray-500 hover:text-green-300 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Copy"
      >
        <Copy className="h-3.5 w-3.5" />
      </button>
      {copied && (
        <span className="absolute top-2 right-8 text-xs text-green-400">Copied!</span>
      )}
    </div>
  );
}

/** Hyper-V steps for a Windows server: a small Ubuntu VM on the LAN runs the same Linux agent (checked against Microsoft and Docker docs, 2026-10-29). */
export const windowsVmStep = `# PowerShell as Administrator on the Windows server
# 1. Install Hyper-V (the server restarts)
Install-WindowsFeature -Name Hyper-V -IncludeManagementTools -Restart

# 2. Connect VMs to the network the agent will scan.
#    Use the adapter name shown by Get-NetAdapter. Creating the switch
#    briefly interrupts the server's network; run it from the console.
Get-NetAdapter
$sw = "Vantage LAN"
New-VMSwitch -Name $sw -NetAdapterName "Ethernet" -AllowManagementOS $true

# 3. Create the VM from the Ubuntu Server 24.04 LTS ISO
#    (download it from ubuntu.com first; adjust the ISO path)
$vm = "vantage-agent"
$vhd = "C:\\Hyper-V\\vantage-agent.vhdx"
$iso = "C:\\ISO\\ubuntu-24.04-live-server-amd64.iso"
New-VHD -Path $vhd -SizeBytes 40GB -Dynamic
New-VM -Name $vm -Generation 2 -MemoryStartupBytes 4GB -VHDPath $vhd
Connect-VMNetworkAdapter -VMName $vm -SwitchName $sw
Set-VMProcessor -VMName $vm -Count 2
Set-VMFirmware -VMName $vm -SecureBootTemplate MicrosoftUEFICertificateAuthority
Add-VMDvdDrive -VMName $vm -Path $iso
Set-VMFirmware -VMName $vm -FirstBootDevice (Get-VMDvdDrive -VMName $vm)
Set-VM -Name $vm -AutomaticStartAction Start
Start-VM -Name $vm`;

export const dockerEngineStep = `# In the Ubuntu VM: install Docker Engine (docs.docker.com/engine/install/ubuntu)
# Short lines on purpose: long pasted lines can be split by the terminal.
sudo apt update
sudo apt install -y ca-certificates curl
K=/etc/apt/keyrings/docker.asc
sudo install -m 0755 -d /etc/apt/keyrings
sudo curl -fsSL https://download.docker.com/linux/ubuntu/gpg -o $K
sudo chmod a+r $K

# Add Docker's repository, then check that Suites and Architectures are filled in
F=/etc/apt/sources.list.d/docker.sources
C=$(. /etc/os-release; echo $VERSION_CODENAME)
A=$(dpkg --print-architecture)
echo "Types: deb" | sudo tee $F
echo "URIs: https://download.docker.com/linux/ubuntu" | sudo tee -a $F
echo "Suites: $C" | sudo tee -a $F
echo "Components: stable" | sudo tee -a $F
echo "Architectures: $A" | sudo tee -a $F
echo "Signed-By: $K" | sudo tee -a $F
cat $F

sudo apt update
sudo apt install -y docker-ce docker-ce-cli containerd.io
sudo apt install -y docker-buildx-plugin docker-compose-plugin
sudo usermod -aG docker $USER   # sign out and back in afterwards

# Copy the agent package from Windows (PowerShell on the server):
# scp .\\vantage-agent-<version>.tar.gz <ubuntu-user>@<vm-ip>:~`;

export function buildCommands(agentVersion: string) {
  return coreApiMode ? `tar -xzf vantage-agent-${agentVersion}.tar.gz
cd vantage-agent
docker build -f agent/Dockerfile \\
  --build-arg AGENT_VERSION=${agentVersion} -t vantage-agent:${agentVersion} .` : `docker pull velosynt/agent:${agentVersion}`;
}

/** docker-compose.yml for one agent, with its real key and connection type filled in. */
export function composeFile(platformUrl: string, agentVersion: string, apiKey: string, connectionType: string) {
  return `services:
  vantage-agent:
    image: ${coreApiMode ? 'vantage-agent' : 'velosynt/agent'}:${agentVersion}
    restart: unless-stopped
    network_mode: host
    environment:
      PLATFORM_URL: "${platformUrl}"
      AGENT_API_KEY: "${apiKey}"
      AGENT_CONNECTION_TYPE: "${connectionType}"
    volumes:
      - nuclei-templates:/root/nuclei-templates
      - agent-state:/app/state
volumes:
  nuclei-templates:
  agent-state:`;
}

/** Upgrade in the fixed vantage-agent folder: replace the code, rebuild, point compose at the new image, restart. */
export function upgradeCommands(version: string) {
  return `# Next to the vantage-agent folder, with the new package downloaded:
rm -rf vantage-agent/agent vantage-agent/backend
tar -xzf vantage-agent-${version}.tar.gz
cd vantage-agent
docker build -f agent/Dockerfile \\
  --build-arg AGENT_VERSION=${version} -t vantage-agent:${version} .
V=${version}
sed -i "s|image: vantage-agent:.*|image: vantage-agent:$V|" docker-compose.yml
docker compose up -d`;
}

/** One-time move for agents installed before 1.7.0 (named velosynt-agent, one folder per version). */
export function migrationCommands(oldVersion: string, version: string) {
  return `# 1. Stop the old agent (its folder is named after its version)
cd velosynt-agent-${oldVersion}
docker compose down
cd ..

# 2. Install the new agent next to it
tar -xzf vantage-agent-${version}.tar.gz
cd vantage-agent
docker build -f agent/Dockerfile \\
  --build-arg AGENT_VERSION=${version} -t vantage-agent:${version} .`;
}

export function startCommands() {
  return `${coreApiMode ? '# In the vantage-agent folder, next to docker-compose.yml' : '# In the folder with docker-compose.yml'}
docker compose up -d

# Follow the logs to confirm the connection (Ctrl+C to stop following):
docker compose logs -f`;
}

export function DownloadPackageButton({ agentVersion }: { agentVersion: string }) {
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  async function download() {
    setDownloading(true); setError('');
    try {
      const blob = await downloadAgentPackage(); const url = URL.createObjectURL(blob); const link = document.createElement('a');
      link.href = url; link.download = `vantage-agent-${agentVersion}.tar.gz`; link.click(); URL.revokeObjectURL(url);
    } catch (failure) { setError(apiError(failure)); } finally { setDownloading(false); }
  }
  return <>
    <button type="button" className="btn-secondary" onClick={download} disabled={downloading}>{downloading ? 'Downloading...' : 'Download agent package'}</button>
    {error && <p role="alert" className="mt-1 text-sm text-red-700">{error}</p>}
  </>;
}
